import { useEffect, useState } from 'react';
import {
  DEFAULT_TIMER_MINUTES,
  MAX_TIMER_MINUTES,
  MIN_TIMER_MINUTES,
  parseTimerMinutes,
} from '@filmsloth/shared';
import { useT } from '../i18n/index.js';

interface TimerSettingProps {
  /** The agreed length, or null for a round that runs as long as it likes. */
  minutes: number | null;
  canEdit: boolean;
  onChange: (minutes: number | null) => void;
}

/**
 * How long the group gives itself, decided before anybody swipes.
 *
 * Off unless somebody asks for it: a countdown nobody chose would be the app
 * hurrying a group that came here to take its time. Switching it on starts at
 * five minutes, which is about one round of cards for a full room.
 */
export function TimerSetting({ minutes, canEdit, onChange }: TimerSettingProps) {
  const t = useT();

  // What is in the box is not yet what the group agreed to: a half-typed "1"
  // on the way to "15" must not commit a one-minute round.
  const [draft, setDraft] = useState(String(minutes ?? DEFAULT_TIMER_MINUTES));
  useEffect(() => {
    if (minutes !== null) setDraft(String(minutes));
  }, [minutes]);

  if (!canEdit) {
    return (
      <span className="muted small">
        {minutes === null ? t('timer.offSummary') : t('timer.summary', { count: minutes })}
      </span>
    );
  }

  // An emptied box reads as the default rather than as an error: there is a
  // sensible answer to "how long", and making somebody type it again is not it.
  const commit = () => {
    const next = parseTimerMinutes(draft);
    setDraft(String(next));
    if (next !== minutes) onChange(next);
  };

  return (
    <div className="stack stack--tight">
      <label className="checkbox-item">
        <input
          type="checkbox"
          checked={minutes !== null}
          onChange={(event) => onChange(event.target.checked ? parseTimerMinutes(draft) : null)}
        />
        <span>{t('timer.enable')}</span>
      </label>

      {minutes !== null ? (
        <div className="row timer-setting__row">
          <input
            className="input timer-setting__minutes"
            type="number"
            inputMode="numeric"
            min={MIN_TIMER_MINUTES}
            max={MAX_TIMER_MINUTES}
            value={draft}
            aria-label={t('timer.minutesLabel')}
            onChange={(event) => setDraft(event.target.value)}
            onBlur={commit}
            onKeyDown={(event) => {
              if (event.key !== 'Enter') return;
              event.preventDefault();
              commit();
            }}
          />
          <span className="muted small">{t('timer.minutesUnit')}</span>
        </div>
      ) : null}

      <span className="muted small">{t('timer.hint')}</span>
    </div>
  );
}
